import { useEffect, useState } from 'react';
import { agentAvailabilityAPI, meetingTypesAPI } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Clock, Calendar, Settings, Save, RefreshCw, Plus, Trash2, 
  CalendarOff, CheckCircle2, Video
} from 'lucide-react';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = { monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };

export const AgentSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [unavailableBlocks, setUnavailableBlocks] = useState([]);
  const [newBlock, setNewBlock] = useState({ start_datetime: '', end_datetime: '', reason: '', block_type: 'personal' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [availRes, typesRes, blocksRes] = await Promise.all([
        agentAvailabilityAPI.get(),
        meetingTypesAPI.getAll(),
        agentAvailabilityAPI.getBlocks()
      ]);
      
      // Ensure working_hours has all 7 days
      const availData = availRes.data || {};
      const existingHours = availData.working_hours || [];
      
      // Create a map of existing hours by day
      const hoursMap = {};
      existingHours.forEach(h => { hoursMap[h.day] = h; });
      
      // Ensure all 7 days exist
      availData.working_hours = DAYS.map(d => hoursMap[d] || {
        day: d,
        is_working: d !== 'saturday' && d !== 'sunday',
        start_time: '09:00',
        end_time: '17:00'
      });
      
      setAvailability(availData);
      setMeetingTypes(typesRes.data || []);
      setUnavailableBlocks(blocksRes.data || []);
    } catch (error) {
      // Initialize with defaults if fetch fails
      setAvailability({
        working_hours: DAYS.map(d => ({
          day: d,
          is_working: d !== 'saturday' && d !== 'sunday',
          start_time: '09:00',
          end_time: '17:00'
        })),
        meeting_types: [],
        is_accepting_bookings: true,
        buffer_minutes: 15,
        max_daily_meetings: 8
      });
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await agentAvailabilityAPI.update(availability);
      toast.success('Settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateWorkingHours = (day, field, value) => {
    setAvailability(prev => {
      if (!prev) return prev;
      // Initialize working_hours if it doesn't exist
      const currentHours = prev.working_hours || DAYS.map(d => ({
        day: d,
        is_working: d !== 'saturday' && d !== 'sunday',
        start_time: '09:00',
        end_time: '17:00'
      }));
      
      return {
        ...prev,
        working_hours: currentHours.map(h => 
          h.day === day ? { ...h, [field]: value } : h
        )
      };
    });
  };

  const toggleMeetingType = (typeId) => {
    setAvailability(prev => {
      const current = prev.meeting_types || [];
      const updated = current.includes(typeId)
        ? current.filter(id => id !== typeId)
        : [...current, typeId];
      return { ...prev, meeting_types: updated };
    });
  };

  const handleAddBlock = async () => {
    if (!newBlock.start_datetime || !newBlock.end_datetime) {
      toast.error('Please select start and end times');
      return;
    }
    try {
      await agentAvailabilityAPI.createBlock({
        ...newBlock,
        start_datetime: new Date(newBlock.start_datetime).toISOString(),
        end_datetime: new Date(newBlock.end_datetime).toISOString()
      });
      toast.success('Time blocked');
      setNewBlock({ start_datetime: '', end_datetime: '', reason: '', block_type: 'personal' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add block');
    }
  };

  const handleDeleteBlock = async (blockId) => {
    try {
      await agentAvailabilityAPI.deleteBlock(blockId);
      toast.success('Block removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete block');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="agent-settings-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">My Settings</h1>
          <p className="text-muted-foreground mt-1">Configure your availability and meeting preferences</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
          {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      <Tabs defaultValue="hours" className="space-y-6">
        <TabsList>
          <TabsTrigger value="hours">
            <Clock className="h-4 w-4 mr-2" />
            Working Hours
          </TabsTrigger>
          <TabsTrigger value="meetings">
            <Video className="h-4 w-4 mr-2" />
            Meeting Types
          </TabsTrigger>
          <TabsTrigger value="blocks">
            <CalendarOff className="h-4 w-4 mr-2" />
            Time Off
          </TabsTrigger>
          <TabsTrigger value="preferences">
            <Settings className="h-4 w-4 mr-2" />
            Preferences
          </TabsTrigger>
        </TabsList>

        {/* Working Hours Tab */}
        <TabsContent value="hours">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Working Hours
              </CardTitle>
              <CardDescription>Set your available hours for each day of the week</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {DAYS.map(day => {
                const hours = availability?.working_hours?.find(h => h.day === day) || {};
                return (
                  <div key={day} className="flex items-center gap-4 p-3 border rounded-lg">
                    <div className="w-32">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={hours.is_working !== false}
                          onCheckedChange={(checked) => updateWorkingHours(day, 'is_working', checked)}
                        />
                        <span className="font-medium">{DAY_LABELS[day]}</span>
                      </div>
                    </div>
                    {hours.is_working !== false ? (
                      <div className="flex items-center gap-2 flex-1">
                        <Input
                          type="time"
                          value={hours.start_time || '09:00'}
                          onChange={(e) => updateWorkingHours(day, 'start_time', e.target.value)}
                          className="w-32"
                        />
                        <span className="text-muted-foreground">to</span>
                        <Input
                          type="time"
                          value={hours.end_time || '17:00'}
                          onChange={(e) => updateWorkingHours(day, 'end_time', e.target.value)}
                          className="w-32"
                        />
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">Day off</span>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Meeting Types Tab */}
        <TabsContent value="meetings">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Video className="h-5 w-5" />
                Meeting Types I Handle
              </CardTitle>
              <CardDescription>Select which types of meetings customers can book with you</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {meetingTypes.map(type => {
                  const isSelected = availability?.meeting_types?.includes(type.id);
                  return (
                    <div
                      key={type.id}
                      onClick={() => toggleMeetingType(type.id)}
                      className={`p-4 border rounded-lg cursor-pointer transition-all ${
                        isSelected ? 'border-primary bg-primary/5' : 'hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge className={type.color}>{type.category}</Badge>
                            <span className="font-medium">{type.name}</span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {type.duration_minutes} minutes
                          </p>
                        </div>
                        {isSelected && <CheckCircle2 className="h-5 w-5 text-primary" />}
                      </div>
                    </div>
                  );
                })}
              </div>
              {meetingTypes.length === 0 && (
                <p className="text-center text-muted-foreground py-8">
                  No meeting types available. Contact your admin to create meeting types.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Time Off / Blocks Tab */}
        <TabsContent value="blocks">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarOff className="h-5 w-5" />
                Blocked Time / Time Off
              </CardTitle>
              <CardDescription>Block specific times when you're unavailable for meetings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Add New Block */}
              <div className="p-4 border rounded-lg bg-slate-50 space-y-4">
                <h4 className="font-medium">Add Time Block</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Start</Label>
                    <Input
                      type="datetime-local"
                      value={newBlock.start_datetime}
                      onChange={(e) => setNewBlock(prev => ({ ...prev, start_datetime: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End</Label>
                    <Input
                      type="datetime-local"
                      value={newBlock.end_datetime}
                      onChange={(e) => setNewBlock(prev => ({ ...prev, end_datetime: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select value={newBlock.block_type} onValueChange={(v) => setNewBlock(prev => ({ ...prev, block_type: v }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vacation">Vacation</SelectItem>
                        <SelectItem value="sick">Sick Leave</SelectItem>
                        <SelectItem value="personal">Personal</SelectItem>
                        <SelectItem value="meeting">External Meeting</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Reason (optional)</Label>
                    <Input
                      value={newBlock.reason}
                      onChange={(e) => setNewBlock(prev => ({ ...prev, reason: e.target.value }))}
                      placeholder="Optional note"
                    />
                  </div>
                </div>
                <Button onClick={handleAddBlock}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Block
                </Button>
              </div>

              {/* Existing Blocks */}
              <div className="space-y-2">
                <h4 className="font-medium">Scheduled Time Off</h4>
                {unavailableBlocks.length > 0 ? (
                  <div className="space-y-2">
                    {unavailableBlocks.map(block => (
                      <div key={block.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{block.block_type}</Badge>
                            <span className="font-medium">
                              {format(new Date(block.start_datetime), 'MMM d, yyyy h:mm a')}
                            </span>
                            <span className="text-muted-foreground">to</span>
                            <span className="font-medium">
                              {format(new Date(block.end_datetime), 'MMM d, yyyy h:mm a')}
                            </span>
                          </div>
                          {block.reason && <p className="text-sm text-muted-foreground mt-1">{block.reason}</p>}
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteBlock(block.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4">No time blocks scheduled</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Preferences Tab */}
        <TabsContent value="preferences">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Booking Preferences
              </CardTitle>
              <CardDescription>Configure how customers can book meetings with you</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <Label className="text-base">Accept Bookings</Label>
                  <p className="text-sm text-muted-foreground">Allow customers to book meetings with you</p>
                </div>
                <Switch
                  checked={availability?.is_accepting_bookings !== false}
                  onCheckedChange={(checked) => setAvailability(prev => ({ ...prev, is_accepting_bookings: checked }))}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Buffer Between Meetings (minutes)</Label>
                  <Select
                    value={String(availability?.buffer_minutes || 15)}
                    onValueChange={(v) => setAvailability(prev => ({ ...prev, buffer_minutes: parseInt(v) }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">No buffer</SelectItem>
                      <SelectItem value="5">5 minutes</SelectItem>
                      <SelectItem value="10">10 minutes</SelectItem>
                      <SelectItem value="15">15 minutes</SelectItem>
                      <SelectItem value="30">30 minutes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Max Daily Meetings</Label>
                  <Select
                    value={String(availability?.max_daily_meetings || 8)}
                    onValueChange={(v) => setAvailability(prev => ({ ...prev, max_daily_meetings: parseInt(v) }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="4">4 meetings</SelectItem>
                      <SelectItem value="6">6 meetings</SelectItem>
                      <SelectItem value="8">8 meetings</SelectItem>
                      <SelectItem value="10">10 meetings</SelectItem>
                      <SelectItem value="12">12 meetings</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AgentSettingsPage;
